import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import {
  isPrefixItem,
  isAccessorItem,
  isEventItem,
  isActionItem,
  type VocabularyEnumKey,
  type VocabularyItem,
} from './types'
import { VocabularyEmpty, VocabularyNoMatch } from './vocabulary-states'
import { useStudioLocale } from '@/i18n/use-studio-locale'

export interface VocabularyTableProps {
  enumKey: VocabularyEnumKey
  items: VocabularyItem[]      // already filtered by search
  totalBeforeFilter: number
  searchQuery: string
  onClearSearch: () => void
}

type VocabularyCopy = { colMode: string; colSemantics: string; colCategory: string; colReturns: string; colDomain: string; colDescription: string }

/** Column headings per enum, read from the dictionary rather than frozen here. */
function metaFor(
  enumKey: VocabularyEnumKey,
  copy: VocabularyCopy,
): { col2: string; col3: string } {
  switch (enumKey) {
    case 'prefix':
      return { col2: copy.colMode, col3: copy.colSemantics }
    case 'accessor':
      return { col2: copy.colCategory, col3: copy.colReturns }
    case 'event':
      return { col2: copy.colDomain, col3: copy.colDescription }
    case 'action':
      return { col2: copy.colCategory, col3: copy.colDescription }
  }
}

export function VocabularyTable({
  enumKey,
  items,
  totalBeforeFilter,
  searchQuery,
  onClearSearch,
}: VocabularyTableProps) {
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.vocabularyPanel
  const meta = metaFor(enumKey, copy)
  if (totalBeforeFilter === 0) return <VocabularyEmpty />
  if (items.length === 0 && searchQuery.trim().length > 0) {
    return <VocabularyNoMatch query={searchQuery} onClear={onClearSearch} />
  }


  return (
    <div className="flex h-full flex-col overflow-hidden">
      <Card className="flex-1 overflow-hidden p-0">
        <div className="flex items-center gap-4 border-b border-border bg-muted/30 px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <div className="w-[160px]">{copy.colName}</div>
          <div className="w-[140px]">{meta.col2}</div>
          <div className="flex-1">{meta.col3}</div>
        </div>
        <div className="overflow-y-auto">
          {items.map((item) => (
            <Row key={item.iri} item={item} />
          ))}
        </div>
      </Card>
    </div>
  )
}

interface RowProps {
  item: VocabularyItem
}

function Row({ item }: RowProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-4 border-b border-border/40 px-4 py-2 text-sm transition-colors hover:bg-muted/20',
      )}
    >
      <div className="w-[160px] truncate font-mono text-[13px]">{item.name}</div>
      <div className="w-[140px] truncate text-xs text-muted-foreground">
        {renderMeta(item)}
      </div>
      <div className="flex-1 truncate text-sm text-foreground">
        {renderDescription(item)}
      </div>
    </div>
  )
}

function renderMeta(item: VocabularyItem): React.ReactNode {
  if (isPrefixItem(item)) return item.readOnly ? 'RO' : ''
  if (isAccessorItem(item)) return item.category
  if (isEventItem(item)) return item.domain
  if (isActionItem(item)) return item.category
  return ''
}

function renderDescription(item: VocabularyItem): string {
  if (isPrefixItem(item)) return item.semantics
  if (isAccessorItem(item)) return item.returnKind
  if (isEventItem(item)) return item.description ?? ''
  if (isActionItem(item)) return item.description ?? ''
  return ''
}
